import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '../../services/loanService';
import { useAuth } from '../../hooks/useAuth';
import { KYCStatusBadge } from '../../components/common/StatusBadge';
import { formatDate, formatCurrency } from '../../utils/helpers';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const FILTERS = ['', 'pending', 'under_review', 'approved', 'rejected', 'resubmit'];

function DocLink({ label, url }) {
  if (!url) return <span style={{ color: 'var(--text-3)' }}>—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer"
      style={{ color: 'var(--primary)', fontSize: '0.8rem', textDecoration: 'underline' }}>
      {label} ↗
    </a>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );
}

// ── Approval-ref success screen (same pattern as loan actions) ────────────────
function ApprovalRefScreen({ approvalRef, message, onClose }) {
  return (
    <div style={{ textAlign: 'center', padding: '1rem 0' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✓</div>
      <h3 style={{ fontWeight: 700, color: 'var(--primary)', marginBottom: '0.4rem' }}>
        Submitted for Checker Review
      </h3>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '1rem' }}>{message}</p>
      <div style={{ background: 'var(--surface-alt)', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: 4 }}>
          Approval Reference
        </div>
        <div style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--primary)', fontSize: '1rem' }}>
          {approvalRef}
        </div>
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginBottom: '1rem' }}>
        A checker must approve this in the Approval Queue before the KYC status changes.
      </p>
      <button className="btn btn-primary" style={{ width: '100%' }} onClick={onClose}>
        Done
      </button>
    </div>
  );
}

export default function KYCReview() {
  const qc = useQueryClient();
  const { isMaker, isChecker, reviewerType } = useAuth();

  const [filter, setFilter] = useState('pending');
  const [selectedRow, setSelectedRow] = useState(null);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [approvalResult, setApprovalResult] = useState(null); // { ref, message }

  // List
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['admin-kyc', filter],
    queryFn: () => adminService.getAllKYC(filter ? { status: filter } : {}).then(r => r.data.data),
  });

  // Full detail for selected row
  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['admin-kyc-detail', selectedRow?.id],
    queryFn: () => adminService.getKYCDetail(selectedRow.id).then(r => r.data.data),
    enabled: !!selectedRow?.id,
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, status, rejection_reason }) =>
      adminService.reviewKYC(id, { status, rejection_reason }),
    onSuccess: (r, vars) => {
      qc.invalidateQueries({ queryKey: ['admin-kyc'] });
      qc.invalidateQueries({ queryKey: ['admin-kyc-detail', vars.id] });
      if (r.data.approval_ref) {
        setApprovalResult({ ref: r.data.approval_ref, message: r.data.message });
      } else {
        setSelectedRow(prev => prev ? { ...prev, status: vars.status } : null);
        setReason('');
        setErr('');
      }
    },
    onError: (e) => setErr(e.response?.data?.message || 'Review action failed.'),
  });

  const kycs = listData?.results || listData || [];
  const panelStatus = detail?.status ?? selectedRow?.status;

  const handleDecide = (status) => {
    if ((status === 'rejected' || status === 'resubmit') && !reason.trim()) {
      setErr('A reason is required when rejecting or requesting resubmission.');
      return;
    }
    reviewMutation.mutate({ id: selectedRow.id, status, rejection_reason: reason });
  };

  const handleCloseApproval = () => {
    setApprovalResult(null);
    setSelectedRow(null);
    setReason('');
    setErr('');
  };

  // Determine if the current user can submit a review decision
  // No reviewer_type set = legacy / unrestricted access
  const canReview = !reviewerType || isMaker;

  return (
    <div>
      <div className="page-header">
        <h1>KYC Review</h1>
        <p>Verify customer identity documents — maker submits decision, checker gives final approval</p>
      </div>

      {/* Role banner */}
      {reviewerType && (
        <div className="alert alert-info" style={{ maxWidth: 680, marginBottom: '1.25rem' }}>
          <span>⚖</span>
          <span style={{ fontSize: '0.85rem' }}>
            {isMaker && !isChecker && (
              <>You are a <strong>Maker</strong>. Your review decision will be sent to the Approval Queue for a checker to confirm before taking effect.</>
            )}
            {isChecker && !isMaker && (
              <>You are a <strong>Checker</strong>. KYC decisions submitted by makers appear in the{' '}
                <a href="/admin/approvals" style={{ color: 'var(--primary)', fontWeight: 600 }}>Approval Queue</a>{' '}
                for your final sign-off. This page is read-only for your role.
              </>
            )}
            {isMaker && isChecker && (
              <>You have <strong>Maker + Checker</strong> privileges. Your reviews create an approval request; you may also approve others' requests in the Approval Queue.</>
            )}
          </span>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {FILTERS.map(s => (
          <button key={s}
            className={`btn ${filter === s ? 'btn-primary' : 'btn-ghost'}`}
            style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem' }}
            onClick={() => { setFilter(s); setSelectedRow(null); setApprovalResult(null); }}>
            {s ? s.replace(/_/g, ' ') : 'All'}
          </button>
        ))}
      </div>

      {listLoading ? <LoadingSpinner /> : (
        <div style={{ display: 'grid', gridTemplateColumns: selectedRow ? '1fr 1.2fr' : '1fr', gap: '1rem', alignItems: 'start' }}>

          {/* List */}
          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Customer</th><th>Status</th><th>Submitted</th><th></th></tr>
                </thead>
                <tbody>
                  {kycs.length === 0 ? (
                    <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>No records found.</td></tr>
                  ) : kycs.map(kyc => (
                    <tr key={kyc.id}
                      style={{ cursor: 'pointer', background: selectedRow?.id === kyc.id ? 'var(--primary-light)' : 'inherit' }}
                      onClick={() => { setSelectedRow(kyc); setReason(''); setErr(''); setApprovalResult(null); }}>
                      <td>
                        <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>{kyc.user_name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>{kyc.user_email}</div>
                      </td>
                      <td><KYCStatusBadge status={kyc.status} /></td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--text-2)' }}>{formatDate(kyc.submitted_at)}</td>
                      <td style={{ color: 'var(--primary)', fontWeight: 600 }}>→</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Detail panel */}
          {selectedRow && (
            <div className="card">
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ fontWeight: 700, marginBottom: '0.2rem' }}>{selectedRow.user_name}</h3>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{selectedRow.user_email}</div>
                </div>
                <KYCStatusBadge status={panelStatus} />
              </div>

              {/* Approval-ref success */}
              {approvalResult ? (
                <ApprovalRefScreen
                  approvalRef={approvalResult.ref}
                  message={approvalResult.message}
                  onClose={handleCloseApproval}
                />
              ) : detailLoading ? (
                <div style={{ padding: '2rem 0', textAlign: 'center' }}><LoadingSpinner /></div>
              ) : detail ? (
                <>
                  {/* KYC fields */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                    <Field label="BVN" value={detail.bvn} />
                    <Field label="NIN" value={detail.nin} />
                    <Field label="ID Type" value={detail.id_type?.replace(/_/g, ' ')} />
                    <Field label="ID Number" value={detail.id_number} />
                    <Field label="Employment" value={detail.employment_status?.replace(/_/g, ' ')} />
                    <Field label="Monthly Income" value={detail.monthly_income ? formatCurrency(detail.monthly_income) : null} />
                    <Field label="Bank" value={detail.bank_name} />
                    <Field label="Account No." value={detail.account_number} />
                    <Field label="Employer" value={detail.employer_name} />
                    <Field label="Submitted" value={formatDate(detail.submitted_at)} />
                  </div>

                  {/* Documents */}
                  <div style={{ borderTop: '1px solid var(--border)', paddingTop: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Documents</div>
                    <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                      <DocLink label="Selfie" url={detail.selfie} />
                      <DocLink label="Proof of Address" url={detail.proof_of_address} />
                      {detail.id_front && <DocLink label="ID Front" url={detail.id_front} />}
                      {detail.id_back && <DocLink label="ID Back" url={detail.id_back} />}
                    </div>
                  </div>

                  {/* Actions */}
                  {['pending', 'under_review'].includes(panelStatus) ? (
                    canReview ? (
                      <>
                        {isMaker && (
                          <div className="alert alert-info" style={{ marginBottom: '0.75rem' }}>
                            <span>⚖</span>
                            <span style={{ fontSize: '0.8rem' }}>
                              Your decision will create a checker approval request before taking effect.
                            </span>
                          </div>
                        )}
                        {err && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{err}</span></div>}
                        <div className="form-group">
                          <label className="form-label">Rejection / Resubmit Reason</label>
                          <textarea className="form-input" rows={2} value={reason}
                            onChange={e => { setReason(e.target.value); setErr(''); }}
                            placeholder="Required when rejecting or requesting resubmission…" />
                        </div>
                        <div style={{ display: 'flex', gap: '0.6rem' }}>
                          <button className="btn btn-primary" style={{ flex: 1 }}
                            disabled={reviewMutation.isPending}
                            onClick={() => handleDecide('approved')}>
                            {reviewMutation.isPending ? '…' : isMaker ? 'Recommend Approval' : 'Approve'}
                          </button>
                          <button className="btn btn-ghost" style={{ flex: 1, color: '#F5A623', borderColor: '#F5A62340' }}
                            disabled={reviewMutation.isPending}
                            onClick={() => handleDecide('resubmit')}>
                            Request Resubmit
                          </button>
                          <button className="btn btn-ghost" style={{ flex: 1, color: '#FF4757', borderColor: '#FF475740' }}
                            disabled={reviewMutation.isPending}
                            onClick={() => handleDecide('rejected')}>
                            {isMaker ? 'Recommend Rejection' : 'Reject'}
                          </button>
                        </div>
                      </>
                    ) : (
                      /* Checker — read-only with pointer to Approval Queue */
                      <div style={{ padding: '0.875rem', background: 'rgba(91,142,255,0.08)', border: '1px solid rgba(91,142,255,0.2)', borderRadius: 8, fontSize: '0.85rem' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-1)', marginBottom: '0.35rem' }}>Pending Maker Decision</div>
                        <div style={{ color: 'var(--text-2)' }}>
                          Once a maker submits a decision, it will appear in the{' '}
                          <a href="/admin/approvals?module=kyc" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                            Approval Queue
                          </a>{' '}
                          for your final sign-off.
                        </div>
                      </div>
                    )
                  ) : (
                    <div style={{ padding: '0.75rem', background: 'var(--surface-alt)', borderRadius: 8, fontSize: '0.8rem', color: 'var(--text-2)', textAlign: 'center' }}>
                      This submission has been <strong>{panelStatus}</strong>.
                      {detail.rejection_reason && (
                        <div style={{ marginTop: '0.25rem', color: '#FF4757' }}>Reason: {detail.rejection_reason}</div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="alert alert-error"><span>⚠</span><span>Could not load KYC details.</span></div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
